import type { Subscription } from 'rxjs';
import * as THREE from 'three';
import {
    CadThemeMode,
    ThemeEventName,
    type ThemeChangedEventPayload,
} from '../../eventBus';
import { useEvent } from '../../hooks/useEvent';
import { LineMaterialFactory } from '../../material';
import { isImportedModelTopologyHost } from '../../imported-model';
import {
    isFeatureLine,
    isFeatureResultMesh,
    type FeatureLine,
    type FeatureResultMesh,
} from '../../mesh/feature';
import type { Viewer } from '../Viewer';
import { ImportedModelTopologyDisplayEdgeController } from './ImportedModelTopologyDisplayEdgeController';
import { ModelDisplayMode } from './ModelDisplayMode';
import { ModelDisplayUnshadedMaterialController } from './ModelDisplayUnshadedMaterialController';


/**
 * 可被显示模式控制器调整的特征线材质。
 */
interface MutableFeatureLineMaterial {
    /** 材质名称，用于识别默认特征线材质。 */
    name: string;
    /** 材质透明度。 */
    opacity: number;
    /** 是否启用透明渲染。 */
    transparent: boolean;
    /** 是否写入深度。 */
    depthWrite: boolean;
    /** 是否参与深度测试。 */
    depthTest: boolean;
    /** 是否需要通知 three.js 更新材质。 */
    needsUpdate: boolean;
}

/**
 * 模型显示模式拓扑边颜色。
 */
enum ModelDisplayTopologyEdgeColor {
    /** 默认黑色显示边。 */
    Default = 0x000000,
    /** 暗色不着色模式下的白色显示边。 */
    DarkUnshaded = 0xffffff,
}

/**
 * 模型显示模式控制器。
 *
 * 设计说明：
 * 1. 控制器只负责 Viewer 场景的显示模式切换，不创建几何体，也不处理 UI 状态；
 * 2. 带边线上色与不着色都显示拓扑合并边，上色模式隐藏默认边；
 * 3. 不着色模式用内部材质快照临时替换实体 mesh 材质，切回其它模式时恢复原材质。
 */
export class ModelDisplayModeController {
    /** 特征线默认材质名称。 */
    private static readonly FEATURE_LINE_DEFAULT_MATERIAL_NAME = '特征线-默认材质';

    /** 带边线上色模式下默认特征线代理透明度：显示边由合并 LineSegments 承载，代理默认保持不可见。 */
    private static readonly SHADED_WITH_EDGES_FEATURE_LINE_OPACITY = 0;

    /** 上色模式下默认特征线透明度。 */
    private static readonly SHADED_FEATURE_LINE_OPACITY = 0;

    /** Viewer 实例。 */
    private readonly viewer: Viewer;

    /** 导入模型拓扑显示边控制器。 */
    private readonly importedModelTopologyDisplayEdgeController: ImportedModelTopologyDisplayEdgeController;

    /** 不着色实体材质控制器。 */
    private readonly unshadedMaterialController: ModelDisplayUnshadedMaterialController;

    /** 主题事件操作集合。 */
    private readonly eventBus = useEvent();

    /** 主题变化事件订阅。 */
    private readonly themeSubscription: Subscription;

    /** 当前模型显示模式。 */
    private displayMode: ModelDisplayMode = ModelDisplayMode.ShadedWithEdges;

    /** 当前主题模式。 */
    private themeMode: CadThemeMode = CadThemeMode.Light;

    /** 是否已经从宿主容器同步过初始主题。 */
    private hasSyncedInitialThemeMode = false;

    /** 是否正在同步场景显示模式。 */
    private isSyncingSceneDisplayMode = false;

    /**
     * 构造函数。
     *
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        this.viewer = viewer;
        this.importedModelTopologyDisplayEdgeController = new ImportedModelTopologyDisplayEdgeController(viewer);
        this.unshadedMaterialController = new ModelDisplayUnshadedMaterialController();
        this.themeSubscription = this.eventBus.add(
            ThemeEventName.ThemeChanged,
            this.handleThemeChangedEvent,
        );

        // 初始化时先同步材质工厂，确保后续新建 FeatureLine 使用当前显示模式的默认透明度。
        this.applyFeatureLineFactoryDefaultOpacity();
    }

    /**
     * 设置模型显示模式。
     * 材质/边线立即更新，但需等 Viewer 结束源模型访问及实例投影更新后再统一绘制。
     *
     * @param displayMode 目标显示模式。
     */
    public setDisplayMode(displayMode: ModelDisplayMode): void {
        if (this.displayMode === displayMode) {
            // 重复设置时仍做一次同步，避免外部交互刚恢复默认材质后视觉状态漂移。
            this.syncSceneDisplayMode();
            this.viewer.requestRender();
            return;
        }

        this.displayMode = displayMode;
        this.applyFeatureLineFactoryDefaultOpacity();
        this.syncSceneDisplayMode();
        this.viewer.requestRender();
    }

    /**
     * 读取当前模型显示模式。
     *
     * @returns 当前显示模式。
     */
    public getDisplayMode(): ModelDisplayMode {
        return this.displayMode;
    }

    /**
     * 同步当前场景内已有对象的显示模式。
     */
    public syncSceneDisplayMode(): void {
        if (this.isSyncingSceneDisplayMode) {
            return;
        }

        this.isSyncingSceneDisplayMode = true;
        try {
            this.syncInitialThemeModeFromContainer();
            this.applyFeatureLineFactoryDefaultOpacity();
            const topologyDisplayEdgeColor = this.resolveTopologyDisplayEdgeColor();
            this.importedModelTopologyDisplayEdgeController.syncDisplayMode(
                this.displayMode,
                topologyDisplayEdgeColor,
            );
            this.viewer.syncImportedModelInstancedTopologyDisplayEdges(
                this.shouldDisplayTopologyEdges(),
                topologyDisplayEdgeColor,
            );
            this.unshadedMaterialController.syncScene(
                this.viewer.scene,
                this.isUnshadedDisplayMode(),
                this.themeMode,
            );

            // 只遍历主场景中的特征结果体与 FeatureLine，避免修改草图线、预览线和其它辅助对象。
            this.viewer.scene.traverse((object) => {
                if (isFeatureResultMesh(object)) {
                    /** imported pick host 只承担拓扑语义，默认边线由专用普通/实例控制器决定。 */
                    if (!isImportedModelTopologyHost(object)) {
                        this.applyFeatureResultMeshDisplayMode(object);
                    }
                    return;
                }
                if (!isFeatureLine(object)) {
                    return;
                }

                this.applyFeatureLineDisplayMode(object);
            });
        } finally {
            this.isSyncingSceneDisplayMode = false;
        }
    }

    /**
     * 同步单个正式特征结果体的显示模式。
     *
     * 设计说明：
     * 1. MeshManager 新增或替换正式结果体时会立即调用该入口；
     * 2. 这里只处理单个 mesh，避免每次回灌都整场景遍历；
     * 3. 不着色材质和拓扑显示边都必须在同一入口同步，保证新结果体视觉完整。
     * 4. imported topology host 只承担语义，不得由通用 FeatureResultMesh 入口创建默认边线。
     *
     * @param mesh 正式特征结果体。
     */
    public syncFeatureResultMesh(mesh: FeatureResultMesh): void {
        this.syncInitialThemeModeFromContainer();
        if (isImportedModelTopologyHost(mesh)) {
            mesh.setTopologyDisplayEdgesEnabled(false);
            return;
        }
        this.applyFeatureResultMeshDisplayMode(mesh);
        this.unshadedMaterialController.syncFeatureResultMesh(
            mesh,
            this.isUnshadedDisplayMode(),
            this.themeMode,
        );
    }

    /**
     * 恢复单个正式特征结果体被显示模式覆盖的材质。
     *
     * 设计说明：
     * 1. 不着色模式会临时替换实体材质，原始材质由控制器内部快照持有；
     * 2. MeshManager 释放正式结果体前必须先恢复材质槽，确保原始材质仍走统一释放路径；
     * 3. 拓扑显示边仍由 `FeatureResultMesh.clearTopologyVisuals()` 负责释放，避免职责交叉。
     *
     * @param mesh 正式特征结果体。
     */
    public restoreFeatureResultMeshMaterial(mesh: FeatureResultMesh): void {
        this.unshadedMaterialController.restoreMeshMaterial(mesh);
    }

    /**
     * 释放控制器资源。
     */
    public dispose(): void {
        this.themeSubscription.unsubscribe();
        this.importedModelTopologyDisplayEdgeController.dispose();
        this.unshadedMaterialController.dispose();
    }

    /**
     * 将当前显示模式同步到特征线材质工厂。
     */
    private applyFeatureLineFactoryDefaultOpacity(): void {
        LineMaterialFactory.setFeatureLineDefaultOpacity(this.resolveFeatureLineDefaultOpacity());
    }

    /**
     * 应用单条特征线的显示模式。
     *
     * @param featureLine 目标特征线。
     */
    private applyFeatureLineDisplayMode(featureLine: FeatureLine): void {
        const materialList = Array.isArray(featureLine.material)
            ? featureLine.material
            : [featureLine.material];

        materialList.forEach((material) => {
            if (!this.isDefaultFeatureLineMaterial(material)) {
                return;
            }

            // 只调整默认态透明度，交互高亮材质必须由 InteractionStateManager 继续接管。
            material.opacity = this.resolveFeatureLineDefaultOpacity();
            material.transparent = material.opacity < 1;
            material.depthWrite = false;
            material.depthTest = true;
            material.needsUpdate = true;
        });
    }

    /**
     * 应用特征结果体的显示模式。
     *
     * @param mesh 特征结果体。
     */
    private applyFeatureResultMeshDisplayMode(mesh: FeatureResultMesh): void {
        mesh.setTopologyDisplayEdgesEnabled(
            this.shouldDisplayTopologyEdges(),
            this.resolveTopologyDisplayEdgeColor(),
        );
    }

    /**
     * 判断材质是否为默认特征线材质。
     *
     * @param material 待判断材质。
     * @returns 是否为默认特征线材质。
     */
    private isDefaultFeatureLineMaterial(material: unknown): material is MutableFeatureLineMaterial {
        if (!material || typeof material !== 'object') {
            return false;
        }

        return Reflect.get(material, 'name') === ModelDisplayModeController.FEATURE_LINE_DEFAULT_MATERIAL_NAME
            && typeof Reflect.get(material, 'opacity') === 'number'
            && typeof Reflect.get(material, 'transparent') === 'boolean';
    }

    /**
     * 解析当前模式下默认特征线透明度。
     *
     * @returns 默认特征线透明度。
     */
    private resolveFeatureLineDefaultOpacity(): number {
        if (this.displayMode === ModelDisplayMode.ShadedWithEdges) {
            return ModelDisplayModeController.SHADED_WITH_EDGES_FEATURE_LINE_OPACITY;
        }

        return ModelDisplayModeController.SHADED_FEATURE_LINE_OPACITY;
    }

    /**
     * 判断当前是否为不着色模式。
     *
     * @returns 当前显示模式为不着色时返回 `true`。
     */
    private isUnshadedDisplayMode(): boolean {
        return this.displayMode === ModelDisplayMode.Unshaded;
    }

    /**
     * 判断当前显示模式是否需要默认拓扑显示边。
     *
     * @returns 需要显示合并边时返回 `true`。
     */
    private shouldDisplayTopologyEdges(): boolean {
        return this.displayMode === ModelDisplayMode.ShadedWithEdges
            || this.displayMode === ModelDisplayMode.Unshaded;
    }

    /**
     * 解析当前显示模式下的拓扑显示边颜色。
     *
     * @returns 拓扑显示边颜色。
     */
    private resolveTopologyDisplayEdgeColor(): THREE.ColorRepresentation {
        if (this.displayMode === ModelDisplayMode.Unshaded && this.themeMode === CadThemeMode.Dark) {
            return ModelDisplayTopologyEdgeColor.DarkUnshaded;
        }

        return ModelDisplayTopologyEdgeColor.Default;
    }

    /**
     * 订阅主题变化后同步当前显示模式。
     *
     * @param payload 主题变化事件载荷。
     */
    private readonly handleThemeChanged = (
        payload: ThemeChangedEventPayload,
    ): void => {
        this.themeMode = payload.mode;
        this.hasSyncedInitialThemeMode = true;
        this.syncSceneDisplayMode();
        this.viewer.requestRender();
    };

    /**
     * 处理事件总线的弱类型主题载荷。
     *
     * @param payload 主题变化事件载荷。
     */
    private readonly handleThemeChangedEvent = (
        payload: unknown,
    ): void => {
        this.handleThemeChanged(payload as ThemeChangedEventPayload);
    };

    /**
     * 从 Viewer 宿主容器读取初始主题模式。
     *
     * 设计说明：
     * 1. 主题事件不是回放事件，Viewer 可能晚于首次主题派发创建；
     * 2. web-client 已把最终视口背景写到容器样式，cad-core 可通过该稳定边界兜底解析；
     * 3. 一旦收到真实主题事件，就以事件为准，不再重复读取 DOM。
     */
    private syncInitialThemeModeFromContainer(): void {
        if (this.hasSyncedInitialThemeMode || typeof window === 'undefined') {
            return;
        }

        const container = this.viewer.container;
        if (!container) {
            return;
        }

        this.themeMode = this.resolveThemeModeFromBackground(
            window.getComputedStyle(container).backgroundColor,
        );
        this.hasSyncedInitialThemeMode = true;
    }

    /**
     * 根据 CSS 背景色解析主题模式。
     *
     * @param backgroundColor CSS 背景色。
     * @returns 主题模式。
     */
    private resolveThemeModeFromBackground(backgroundColor: string): CadThemeMode {
        const normalizedBackground = backgroundColor.replace(/\s+/g, '').toLowerCase();
        if (
            normalizedBackground === '#000000'
            || normalizedBackground === 'black'
            || normalizedBackground === 'rgb(0,0,0)'
            || normalizedBackground === 'rgba(0,0,0,1)'
        ) {
            return CadThemeMode.Dark;
        }

        return CadThemeMode.Light;
    }
}

